<template>
  <div class="rounded-lg border border-surface">
    <div v-for="row in rows" :key="row.node.taskPath" class="border-b border-surface last:border-b-0">
      <div class="flex items-center gap-2 py-2 pr-3"
           :style="{ paddingLeft: `${row.depth * 1.25 + 0.75}rem` }">
        <button v-if="row.node.children.length > 0"
                class="w-5 shrink-0 text-muted-color hover:text-color"
                type="button"
                @click="toggle(row.node.taskPath)">
          <i :class="collapsed.has(row.node.taskPath) ? 'pi pi-angle-right' : 'pi pi-angle-down'"
             class="text-xs" />
        </button>
        <span v-else class="w-5 shrink-0" />

        <span class="relative flex h-2.5 w-2.5 shrink-0">
          <span v-if="row.node.status === ExecutionStatus.RUNNING"
                class="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-60" />
          <span class="relative inline-flex h-2.5 w-2.5 rounded-full" :class="dotClass(row.node)" />
        </span>

        <span class="truncate text-sm"
              :class="row.node.status === ExecutionStatus.PENDING ? 'text-muted-color' : ''"
              :title="row.node.description">{{ row.node.description || `Task ${row.node.sequence}` }}</span>
        <i v-if="row.node.dynamicTasks"
           v-tooltip.top="'This task generated further tasks while running'"
           class="pi pi-sitemap shrink-0 text-xs text-muted-color" />

        <span class="ml-auto shrink-0 font-mono text-xs text-muted-color">{{ row.node.taskPath }}</span>
        <span class="w-16 shrink-0 text-right text-xs text-muted-color">
          {{ formatDuration(row.node.started, row.node.finished, now) }}
        </span>
        <button v-if="props.expandable?.(row.node)"
                class="w-5 shrink-0 text-muted-color hover:text-color"
                type="button"
                :aria-expanded="detailOpen(row.node)"
                @click="toggleDetail(row.node.taskPath)">
          <i :class="detailOpen(row.node) ? 'pi pi-angle-up' : 'pi pi-angle-down'" class="text-xs" />
        </button>
        <span v-else class="w-5 shrink-0" />
      </div>
      <div v-if="props.expandable?.(row.node) && detailOpen(row.node)"
           class="pb-2 pr-3"
           :style="{ paddingLeft: `${row.depth * 1.25 + 2.5}rem` }">
        <slot name="detail" :node="row.node" />
      </div>

      <div v-if="row.node.progress && row.node.status === ExecutionStatus.RUNNING"
           class="pb-2 pr-3"
           :style="{ paddingLeft: `${row.depth * 1.25 + 2.5}rem` }">
        <ProgressBar :value="row.node.progress.percentageComplete" :show-value="false" class="!h-1.5" />
        <div class="mt-1 truncate text-xs text-muted-color">{{ row.node.progress.message }}</div>
      </div>

      <div v-if="row.node.error"
           class="pb-2 pr-3 text-xs text-red-400"
           :style="{ paddingLeft: `${row.depth * 1.25 + 2.5}rem` }">
        {{ row.node.error }}
      </div>
    </div>

    <div v-if="rows.length === 0" class="p-4 text-sm text-muted-color">
      No tasks discovered yet
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import ProgressBar from 'primevue/progressbar'
import { ExecutionStatus } from '@kinotic-ai/management-api'
import type { JobTaskNode } from './JobTaskNode'
import DatetimeUtil from '../../util/DatetimeUtil'

const formatDuration = DatetimeUtil.formatDuration

interface TreeRow {
  node: JobTaskNode
  depth: number
}

/**
 * The run's full task ledger as an indented, collapsible tree. Rows appear as tasks are
 * discovered, at any depth; now drives the running rows' elapsed time. A row expandable
 * per the given predicate carries the detail slot beneath it, open while the task runs
 * unless toggled shut, and closed otherwise unless toggled open.
 */
const props = defineProps<{
  root: JobTaskNode
  now: number
  expandable?: (node: JobTaskNode) => boolean
}>()

defineSlots<{
  detail(props: { node: JobTaskNode }): unknown
}>()

const collapsed = ref(new Set<string>())
// Paths whose detail the user flipped away from its default of "open while running"
const detailToggled = ref(new Set<string>())

const rows = computed<TreeRow[]>(() => {
  const out: TreeRow[] = []
  const walk = (nodes: JobTaskNode[], depth: number) => {
    for (const node of nodes) {
      out.push({ node, depth })
      if (!collapsed.value.has(node.taskPath)) {
        walk(node.children, depth + 1)
      }
    }
  }
  walk(props.root.children, 0)
  return out
})

function toggle(taskPath: string): void {
  const next = new Set(collapsed.value)
  if (next.has(taskPath)) {
    next.delete(taskPath)
  } else {
    next.add(taskPath)
  }
  collapsed.value = next
}

function detailOpen(node: JobTaskNode): boolean {
  return (node.status === ExecutionStatus.RUNNING) !== detailToggled.value.has(node.taskPath)
}

function toggleDetail(taskPath: string): void {
  const next = new Set(detailToggled.value)
  if (next.has(taskPath)) {
    next.delete(taskPath)
  } else {
    next.add(taskPath)
  }
  detailToggled.value = next
}

function dotClass(node: JobTaskNode): string {
  let ret: string
  if (node.status === ExecutionStatus.COMPLETED) {
    ret = 'bg-emerald-500'
  } else if (node.status === ExecutionStatus.RUNNING) {
    ret = 'bg-sky-400'
  } else if (node.status === ExecutionStatus.FAILED) {
    ret = 'bg-red-500'
  } else if (node.status === ExecutionStatus.CANCELLED) {
    ret = 'bg-amber-500'
  } else {
    ret = 'bg-[var(--p-content-border-color)]'
  }
  return ret
}
</script>
